/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { PredictionsLegend } from 'src/luis/projects/analytics/components/OverallPredictionsCard/OverallChart/Legend/PredictionsLegend';
import { StatsLegend } from 'src/luis/projects/analytics/components/OverallPredictionsCard/OverallChart/Legend/StatsLegend';
import { EmptyRow } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendRowHelpers';

export type LegendProps = {
    /**
     * Flag to indicate if the legend is in comparison mode or not.
     */
    inComparisonMode: boolean;

    /**
     * The correct percentage.
     */
    correctPercentage: number;

    /**
     * The incorrect percentage.
     */
    incorrectPercentage: number;

    /**
     * The unclear percentage.
     */
    unclearPercentage: number;

    /**
     * The difference between the correct percentages of the current version and the compared version.
     */
    correctPercentageDiff?: number;

    /**
     * The difference between the incorrect percentages of the current version and the compared version.
     */
    incorrectPercentageDiff?: number;

    /**
     * The difference between the unclear percentages of the current version and the compared version.
     */
    unclearPercentageDiff?: number;

    /**
     * The number of intents.
     */
    numIntents: number;

    /**
     * The number of entities.
     */
    numEntities: number;

    /**
     * The number of utterances.
     */
    numUtterances: number;

    /**
     * The difference between the numbers of intents of the current version and the compared version.
     */
    numIntentsDiff?: number;

    /**
     * The difference between the numbers of entities of the current version and the compared version.
     */
    numEntitiesDiff?: number;

    /**
     * The difference between the numbers of utterances of the current version and the compared version.
     */
    numUtterancesDiff?: number;
};

/**
 * Generates the legend rows of the overall donut chart.
 *
 * @param props The properties to fill legend rows.
 */
export const Legend = (props: LegendProps) => {
    const {
        correctPercentage,
        correctPercentageDiff,
        incorrectPercentage,
        incorrectPercentageDiff,
        unclearPercentage,
        unclearPercentageDiff,
        numIntents,
        numIntentsDiff,
        numEntities,
        numEntitiesDiff,
        numUtterances,
        numUtterancesDiff,
        inComparisonMode
    } = props;

    return (
        <div>
            <PredictionsLegend
                inComparisonMode={inComparisonMode}
                correctPercentage={correctPercentage}
                correctPercentageDiff={correctPercentageDiff}
                incorrectPercentage={incorrectPercentage}
                incorrectPercentageDiff={incorrectPercentageDiff}
                unclearPercentage={unclearPercentage}
                unclearPercentageDiff={unclearPercentageDiff}
            />
            <EmptyRow />
            <StatsLegend
                inComparisonMode={inComparisonMode}
                numEntities={numEntities}
                numEntitiesDiff={numEntitiesDiff}
                numIntents={numIntents}
                numIntentsDiff={numIntentsDiff}
                numUtterances={numUtterances}
                numUtterancesDiff={numUtterancesDiff}
            />
        </div>
    );
};
